/**
 * Shared visual primitives for the Governance Hub · Policy Catalogue (S14)
 * and the Policy Detail Drawer.
 *
 * Kept small and stateless — colour/tone tokens follow the same palette
 * used in AgentPrimitives (risk-low/med/high, tralenex, gold, opp).
 */
import React from "react";
import { cn } from "@/lib/utils";

/** Round dot indicating a policy's severity band. */
export function SeverityDot({ severity, className }) {
  const tone =
    severity === "high"
      ? "bg-risk-high"
      : severity === "med"
        ? "bg-risk-med"
        : "bg-risk-low";
  return (
    <span
      aria-label={`Severity ${severity}`}
      data-testid={`policy-severity-dot-${severity}`}
      className={cn("inline-block w-2.5 h-2.5 rounded-full mt-1.5 shrink-0", tone, className)}
    />
  );
}

/** Status pill — `active` (enforced) vs. `draft` (not yet enforced). */
export function PolicyStatusPill({ status }) {
  const isActive = status === "active";
  return (
    <span
      data-testid={`policy-status-${status}`}
      className={cn(
        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold tracking-[0.06em] uppercase",
        isActive
          ? "bg-risk-low/10 text-risk-low"
          : "bg-gold-300/15 text-gold-600",
      )}
    >
      <span className={cn("w-1.5 h-1.5 rounded-full", isActive ? "bg-risk-low" : "bg-gold-600")} />
      {status}
    </span>
  );
}

/** Violation badge — green pill at zero, red at one-or-more. */
export function ViolationsBadge({ count }) {
  if (!count) {
    return (
      <span
        data-testid="policy-violations-clean"
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-risk-low/10 text-risk-low text-meta font-semibold tabular-nums"
      >
        0
      </span>
    );
  }
  return (
    <span
      data-testid="policy-violations-count"
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-risk-high/10 text-risk-high text-meta font-semibold tabular-nums"
    >
      {count}
    </span>
  );
}
